import React, { useState, useEffect } from 'react';
import { landlordAPI } from '../../lib/api';
import { formatDate, formatCurrency } from '../../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { History, Loader2, Calendar, DollarSign, ArrowRight, FileText } from 'lucide-react';

export function LeaseHistoryDialog({ open, onOpenChange, leaseId }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && leaseId) {
      loadHistory();
    }
  }, [open, leaseId]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional mount-only load

  const loadHistory = async () => {
    setLoading(true);
    try {
      const response = await landlordAPI.getLeaseHistory(leaseId);
      setHistory(response.data);
    } catch (error) {
      console.error('Error loading lease history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'archived': return 'bg-gray-100 text-gray-700 dark:bg-gray-800/50 dark:text-gray-400';
      case 'pending': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'terminated': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800/50 dark:text-gray-400';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Lease History
          </DialogTitle>
          <DialogDescription>
            View previous versions and renewals of this lease agreement
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No history available</p>
            </div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />
              
              <div className="space-y-6">
                {history.map((lease, index) => (
                  <div key={lease.lease_id} className="relative pl-14">
                    {/* Timeline dot */}
                    <div className={`absolute left-4 w-5 h-5 rounded-full border-4 ${
                      index === 0 
                        ? 'bg-[#C85A42] border-[#C85A42]/30' 
                        : 'bg-muted border-border'
                    }`} />
                    
                    <Card className={index === 0 ? 'border-[#C85A42]/30' : ''}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold">
                                {index === 0 ? 'Current Lease' : `Version ${history.length - index}`}
                              </h4>
                              <Badge className={getStatusColor(lease.status)}>
                                {lease.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {lease.lease_id}
                            </p>
                          </div>
                          {index === 0 && (
                            <Badge variant="outline" className="bg-[#C85A42]/10 text-[#C85A42] border-[#C85A42]/30">
                              Active
                            </Badge>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            <span>
                              {formatDate(lease.start_date)} <ArrowRight className="w-3 h-3 inline" /> {formatDate(lease.end_date)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium">{formatCurrency(lease.total_monthly_rent)}/mo</span>
                          </div>
                        </div>

                        {lease.additional_fees && lease.additional_fees.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs text-muted-foreground mb-2">Additional Fees:</p>
                            <div className="flex flex-wrap gap-2">
                              {lease.additional_fees.map((fee, feeIndex) => (
                                <Badge key={feeIndex} variant="secondary" className="text-xs">
                                  {fee.name}: {formatCurrency(fee.amount)}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {lease.previous_lease_id && (
                          <p className="text-xs text-muted-foreground mt-3">
                            Renewed from: {lease.previous_lease_id}
                          </p>
                        )}

                        <div className="mt-3 text-xs text-muted-foreground">
                          Created: {formatDate(lease.created_at)}
                          {lease.acknowledged && lease.acknowledged_at && (
                            <span className="ml-3">• Acknowledged: {formatDate(lease.acknowledged_at)}</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
